import type { DraftState, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { EstimateOutcome } from './primerComparison.ts'
import { estimateOutcome } from './primerComparison.ts'
import { hasKeyColumn, primerRevision } from './sheet/sheetMapping.ts'
import { sheetMatchOf } from './sheet/sheetMatching.ts'
import { sheetPrimers } from './sheet/sheetImport.ts'
import { isArchiveOrder } from './archive.ts'
import { isFailedEstimate } from './estimationCore.ts'
import { writtenEstimate } from './primerOrientation.ts'

export type ValidatedSample = { group: WizardSampleGroup; outcome: EstimateOutcome; spelledByLab: boolean }

/**
 * Every sample whose sheet row declares both primers and whose estimate is in, with how the estimator
 * did. Samples still being read, or whose estimation failed, are left out rather than counted as "no estimate".
 */
export const validateEstimates = (draft: DraftState): ValidatedSample[] => {
  const sheet = draft.sheet && hasKeyColumn(draft.sheet.mapping) ? draft.sheet : undefined
  if (!sheet || draft.discovery?.primers.status !== 'found' || isArchiveOrder(draft)) {
    return []
  }
  const match = sheetMatchOf(draft)
  if (!match) {
    return []
  }
  return Object.values(draft.groups).flatMap((group) => {
    const rowIndex = match.byGroup[group.key]
    const estimate = writtenEstimate(draft, group)
    if (rowIndex === undefined || !estimate || isFailedEstimate(estimate)) {
      return []
    }
    const outcome = estimateOutcome(estimate, sheetPrimers(sheet.rows[rowIndex], sheet.mapping))
    return outcome ? [{ group, outcome, spelledByLab: estimate.forward.spelledByLab || estimate.reverse.spelledByLab }] : []
  })
}

/** The samples checked against these primer columns for the first time, which is when they are reported. */
export const newlyValidated = (draft: DraftState): ValidatedSample[] => {
  const revision = draft.sheet ? primerRevision(draft.sheet) : undefined
  return validateEstimates(draft).filter(({ group }) => group.validatedFor !== revision)
}

export type EstimateTally = Record<EstimateOutcome, number> & { total: number }

export const tallyEstimates = (validated: readonly ValidatedSample[]): EstimateTally => {
  const tally: EstimateTally = { correct: 0, tag_mismatch: 0, sheet_untagged: 0, gene_spelling: 0, no_estimate: 0, total: validated.length }
  validated.forEach(({ outcome }) => {
    tally[outcome] += 1
  })
  return tally
}
